import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { BaseModalProps, widthMap } from './Modal';
import { FormErrorAlert, cn } from '..';
import { LoadingState } from './LoadingState';
import { useEffect } from 'react';
import { SuccessMessage } from './Modal';

export type DialogModalProps = BaseModalProps & {
  roundedLarge?: boolean;
  maxHeight?: string;
};

export function DialogModal({
  open,
  onClose,
  title,
  subTitle,
  isLoading,
  children,
  width,
  showSuccess,
  successMessage,
  errorMessage,
  primaryButton,
  roundedLarge,
  stickyFooter,
  className,
  maxHeight,
}: DialogModalProps) {
  const widthCode = width ? widthMap[width] : 'sm:max-w-[650px]';

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, onClose]);

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent
        className={cn(
          'overflow-auto rounded-lg',
          maxHeight ? maxHeight : 'max-h-[100vh] md:max-h-[90vh]',
          stickyFooter
            ? 'mt-12 pb-0 pt-12 sm:my-0 md:pb-0 md:pt-8'
            : 'my-12 py-12 sm:my-0 md:py-8',
          widthCode,
          showSuccess ? 'rounded-t-2xl border-t-4 border-green-600' : '',
          className,
        )}
      >
        <div className={`${!showSuccess ? '' : 'hidden'}`}>
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl font-semibold text-primary-dark md:text-2xl">
              {title}
            </DialogTitle>
            {subTitle && (
              <DialogDescription className="text-sm text-gray-500 md:text-base">
                {subTitle}
              </DialogDescription>
            )}
          </DialogHeader>

          {children}
        </div>

        <LoadingState isLoading={isLoading} />

        {errorMessage && (
          <FormErrorAlert
            mainErrorMessage={errorMessage}
            className="mt-4"
          />
        )}

        <SuccessMessage
          show={showSuccess === true}
          successMessage={successMessage}
        />
        {primaryButton && !showSuccess && (
          <DialogFooter
            className={cn(
              stickyFooter
                ? 'sticky bottom-0 z-50 -mx-8 mt-auto w-[calc(100%+5rem)] overflow-hidden px-12 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:bg-white'
                : 'mb-2 md:mb-0',
            )}
          >
            {primaryButton}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
